import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import apiConfig from '../config/apiConfig';
import { parseExtraOffers, parseUsageBlocks, refusalOf, type ExtraKind, type ExtraOffer, type UsageBlocks } from '../components/billing/usage';
import { planFailure } from '../services/subscriptionService';

// Emails of users who see the floating DevTierSwitcher and can toggle their
// tier in-place. These accounts still go through the REAL backend — the
// widget calls `/api/subscription/admin/set-override-tier` so every
// entitlement check and paywall mirrors what a real user on that tier sees.
// That's the point: we want to feel the Free/Plus/Pro experience, not a
// client-side mock of it.
export const DEV_EMAILS = ['email.benpaulson@gmail.com'];

// Types matching backend
export type SubscriptionTier = 'free' | 'plus' | 'pro' | 'studio';

export interface TierLimits {
  // Usage-based limits (-1 = unlimited, 0 = none). Chat v5 (CONTRACT5 §1): the plan's two measures are chat (a
  // monthly allowance the server keeps in dollars and only ever shows as a bar) and `reports` (a number a month).
  // The old answers / pitch-draft / offer-read / brand-search quotas are retired server-side; their keys stay
  // optional here only so an older reply still types.
  saved_creators: number;        // How many creators can be saved
  /** Reports a month: an offer review, a shortlist of brands, or a file on one brand. */
  reports?: number;
  sponsor_searches?: number;
  creator_analyses?: number;
  creator_entities?: number;
  /** @deprecated retired v5 (Pitch Writer draws from chat). */
  pitch_drafts?: number;
  /** @deprecated retired v5 (chat is a bar, never a count). */
  coach_run?: number;
  /** @deprecated retired v5 (finding brands is a report). */
  prospect_leads?: number;

  // Feature flags
  alerts?: boolean;
  contract_scan?: boolean;
  recommendations?: boolean;
  purchase_insights?: boolean;
  priority_scraping?: boolean;
  best_models?: boolean;
  team_features?: boolean;
}

/** Where the plan comes from (GET /user, B7): Stripe, an access code, the founder's tier switcher, a test account, or
 *  nothing to bill. ABSENT on an older server — then no change control renders for a paid plan (the capability gate). */
export type BillingSource = 'none' | 'stripe' | 'access_code' | 'override' | 'test_account';
/** Stripe's mode, from GET /tiers `billing.mode` (the key's prefix): test keys print a "Test mode" stamp. */
export type BillingMode = 'test' | 'live' | 'off';
/** A move down scheduled for the renewal date. */
export interface PendingChange { tier: SubscriptionTier; on: string }

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  tier_name: string;      // legacy internal label (Free/Plus/Pro/Studio)
  display_name: string;   // user-facing label (Free/Plus/Pro/Studio)
  is_active: boolean;
  limits: TierLimits;
  features: string[];
  usage: Record<string, number>;
  /** The end of this billing month (the next payment, or when a cancel lands); null when there is none. */
  next_renewal?: string | null;
  cancel_at_period_end: boolean;
  // `tier` above is the EFFECTIVE tier. These expose whether it came from a
  // dev override / test-account flag vs. the real Stripe tier — drives the
  // DevTierSwitcher's "override active" state. Present only from the backend.
  override_tier?: SubscriptionTier | null;
  is_test_account?: boolean;
  real_tier?: SubscriptionTier | null;
  // B7 (the plan & billing page): where the plan comes from, Stripe's own status, a scheduled move down, and whether a
  // Stripe customer exists (Card & invoices). Absent from an older server.
  billing_source?: BillingSource;
  status?: string | null;
  pending_change?: PendingChange | null;
  has_billing_account?: boolean;
  /** When the current billing month began (past due: the day the payment was due). Printed only when sent. */
  current_period_start?: string | null;
}

export interface UsageSummaryEntry {
  used: number;
  limit: number;        // -1 = unlimited
  remaining: number;    // -1 = unlimited
  // Every allowance is monthly now (answers on Free moved from 3-ever to 5-a-month); 'lifetime'
  // is kept only so an older server's reply still types, and is printed as such, never as a month.
  window: 'month' | 'lifetime';
  resets_on: string | null;  // ISO date; null for lifetime windows
}

/** Usage types the app never displays, even if a server reply carries them: prospect_leads is
 *  the hidden fair-use ceiling on finding brands (finding brands is an answer), search_probe an
 *  anti-abuse ceiling. The server hides both; this keeps them hidden if one ever slips through. */
export const HIDDEN_USAGE_TYPES: readonly string[] = ['prospect_leads', 'search_probe'];

const visibleUsage = (usage: Record<string, UsageSummaryEntry> | null | undefined): Record<string, UsageSummaryEntry> | null => {
  if (!usage) return null;
  const shown = { ...usage };
  HIDDEN_USAGE_TYPES.forEach((key) => { delete shown[key]; });
  return shown;
};

/** "Oct 1" from the server's ISO reset date, or null when there is none to print. */
export const formatResetOn = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * A stop from the server said in the app's own words, or null when the message isn't one (show it as it came).
 * v5: the chat and reports limits are kept word for word (the server writes them plainly: "You've used this month's
 * chat on Plus. It comes back on Oct 1."); the daily burst guard is never printed with its "midnight UTC" — it is
 * said in the page's words, and the creator's own clock lives where it is shown (billing/usage burstLiftsAt).
 */
export function limitMessage(message: string | null | undefined): string | null {
  const kind = refusalOf(message);
  if (!kind) return null;
  if (kind === 'burst') return 'You’ve asked a lot today, so Bearri is pausing new questions for a few hours. Nothing was used, and this month’s chat isn’t affected.';
  return message!.trim();
}

export interface EntitlementCheck {
  allowed: boolean;
  reason?: string;
  current_usage?: number;
  limit?: number;
  upgrade_tier?: SubscriptionTier;
}

export interface TierInfo {
  tier: SubscriptionTier;
  name: string;           // legacy internal label (Free/Plus/Pro/Studio)
  display_name: string;   // user-facing label (Free/Plus/Pro/Studio)
  price_monthly: number;
  limits: TierLimits;
  features: string[];
  /** v5: "about 120 questions a month" (the bar is the truth; N is always "about"). */
  questions_note?: string | null;
  /** v5: the plan a creator on this tier upgrades to (null on Pro and Studio). */
  next_tier?: SubscriptionTier | null;
}

interface SubscriptionContextType {
  // State
  subscription: SubscriptionInfo | null;
  allTiers: TierInfo[];
  loading: boolean;
  error: string | null;
  isDev: boolean;

  // Read-only quota snapshot (GET /usage-summary) — the FE never checks
  // or records usage itself; it only displays what the server reports.
  usageSummary: Record<string, UsageSummaryEntry> | null;
  usageSummaryLoading: boolean;
  /** v5 (CONTRACT5 §5): the chat bar, the reports count, the plan and what extras cost — null until the server sends
   *  them (THE GATE: no bar, stub, Run cost or extras button renders without it). Never computed here. */
  usage: UsageBlocks | null;
  /** v5: what "keep going" costs, from GET /tiers (the plan page's extras line; public-safe, no account needed). */
  tierExtras: Partial<Record<ExtraKind, ExtraOffer>> | null;
  /** Stripe's mode from GET /tiers (null until it lands, or from an older server). */
  billingMode: BillingMode | null;

  // Actions
  refreshSubscription: () => Promise<void>;
  refreshUsageSummary: () => Promise<void>;
  /** Refill the bar from blocks the server already returned (an extras confirm), with no second fetch. */
  applyUsage: (blocks: UsageBlocks) => void;
  /** Put a plan the server just returned (a change, cancel, resume, confirm or sync) in place, with no second fetch. */
  applySubscription: (info: SubscriptionInfo) => void;
  /** Stripe Checkout for Free → a paid plan. Throws PlanChangeError (has_subscription · comped · checkout_pending · …). */
  createCheckoutSession: (tier: SubscriptionTier) => Promise<string>;
  /** Card & invoices (Stripe's portal); comes back to `returnPath`. Throws PlanChangeError (portal_not_ready · …). */
  openCustomerPortal: (returnPath?: string) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/** The same context, or null outside a provider (a page that only READS the month's measures, and renders without them). */
export const useOptionalSubscription = (): SubscriptionContextType | null => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  // Remount all account state synchronously; a previous account's late
  // entitlement/usage response must never appear in the next account.
  return <AccountSubscriptionProvider key={user?.id || 'signed-out'}>{children}</AccountSubscriptionProvider>;
};

const AccountSubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [allTiers, setAllTiers] = useState<TierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageSummary, setUsageSummary] = useState<Record<string, UsageSummaryEntry> | null>(null);
  const [usageSummaryLoading, setUsageSummaryLoading] = useState(true);
  const [usage, setUsage] = useState<UsageBlocks | null>(null);
  const [tierExtras, setTierExtras] = useState<Partial<Record<ExtraKind, ExtraOffer>> | null>(null);
  const [billingMode, setBillingMode] = useState<BillingMode | null>(null);
  const subscriptionRequest = useRef(0);
  const usageRequest = useRef(0);
  useEffect(() => () => { subscriptionRequest.current++; usageRequest.current++; }, []);

  const getBaseUrl = () => apiConfig.baseUrl;

  // Fetch all tiers on mount
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await fetch(`${getBaseUrl()}/api/subscription/tiers`);
        if (response.ok) {
          const data = await response.json();
          setAllTiers(Array.isArray(data.tiers) ? data.tiers : []);
          // the extras block sits beside the tiers (what "keep going" costs, for the pricing page's band)
          const offers = parseExtraOffers(data.extras);
          setTierExtras(offers && Object.keys(offers).length ? offers : null);
          const mode = data?.billing?.mode;
          setBillingMode(mode === 'test' || mode === 'live' || mode === 'off' ? mode : null);
        }
      } catch (err) {
        console.error('Failed to fetch tiers:', err);
      }
    };
    fetchTiers();
  }, []);

  // Dev accounts see the floating DevTierSwitcher widget. They still hit the
  // real subscription endpoint — `override_tier` on their DB row decides what
  // comes back, so every feature gate/paywall/usage counter behaves exactly
  // as it would for a paying customer on that tier.
  const isDev = !!(user?.primaryEmailAddress?.emailAddress
    && DEV_EMAILS.includes(user.primaryEmailAddress.emailAddress));

  const refreshSubscription = useCallback(async () => {
    const request = ++subscriptionRequest.current;
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      if (!token) throw new Error('Session unavailable');
      const response = await fetch(`${getBaseUrl()}/api/subscription/user/${user.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        if (request !== subscriptionRequest.current) return;
        setSubscription(data);
      } else {
        throw new Error('Failed to fetch subscription');
      }
    } catch (err) {
      if (request !== subscriptionRequest.current) return;
      setError('Could not check your plan. Your subscription has not changed.');
      // Preserve a last confirmed snapshot, never manufacture a Free plan.
    } finally {
      if (request === subscriptionRequest.current) setLoading(false);
    }
  }, [user?.id, getToken]);

  const refreshUsageSummary = useCallback(async () => {
    const request = ++usageRequest.current;
    if (!user?.id) {
      setUsageSummary(null);
      setUsage(null);
      setUsageSummaryLoading(false);
      return;
    }
    try {
      setUsageSummaryLoading(true);
      const token = await getToken();
      if (!token) throw new Error('Session unavailable');
      const response = await fetch(`${getBaseUrl()}/api/subscription/usage-summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        if (request !== usageRequest.current) return;
        setUsageSummary(visibleUsage(data.usage));
        setUsage(parseUsageBlocks(data));
      }
      // Non-OK: keep the last known snapshot — chips degrade gracefully
      // and the server still hard-gates every run regardless.
    } catch (err) {
      console.error('Usage summary fetch error:', err);
    } finally {
      if (request === usageRequest.current) setUsageSummaryLoading(false);
    }
  }, [user?.id, getToken]);

  useEffect(() => {
    if (isLoaded) {
      refreshSubscription();
      refreshUsageSummary();
    }
  }, [isLoaded, refreshSubscription, refreshUsageSummary]);

  // Quota enforcement lives SERVER-side: gated endpoints check + record
  // usage themselves and return 403 (QuotaError in dataService) when spent.
  // The old client-side checkEntitlement/recordUsage round-trips were
  // spoofable and double-counted usage, so they are gone on purpose.

  const applyUsage = useCallback((blocks: UsageBlocks) => {
    // a newer read supersedes anything still in flight
    usageRequest.current++;
    setUsage(blocks);
    setUsageSummaryLoading(false);
  }, []);

  const applySubscription = useCallback((info: SubscriptionInfo) => {
    // the server's answer to a change is the newest plan: anything still in flight is older
    subscriptionRequest.current++;
    setSubscription(info);
    setError(null);
    setLoading(false);
  }, []);

  const createCheckoutSession = async (tier: SubscriptionTier): Promise<string> => {
    if (!user?.id) {
      throw new Error('Not logged in');
    }
    // The endpoint reads the account from the verified Clerk token (never the body): without it the server answers
    // 401 and no checkout opens. It builds the return addresses itself (/app/plan?checkout=done&session_id=…); the ones
    // sent here are the same, for a server that still reads them.
    const token = await getToken();
    if (!token) throw new Error('Session unavailable');

    const response = await fetch(`${getBaseUrl()}/api/subscription/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        tier,
        success_url: `${window.location.origin}/app/plan?checkout=done&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/app/plan?checkout=cancelled`,
      }),
    });

    if (!response.ok) await planFailure(response, 'Checkout couldn’t open. Nothing was charged; try again.');

    const data = await response.json();
    if (typeof data?.checkout_url !== 'string' || !/^https:\/\//.test(data.checkout_url)) {
      throw new Error('Checkout couldn’t open. Nothing was charged; try again.');
    }
    return data.checkout_url;
  };

  const openCustomerPortal = async (returnPath = '/app/plan?from=portal'): Promise<void> => {
    if (!user?.id) {
      throw new Error('Not logged in');
    }

    const token = await getToken();
    if (!token) throw new Error('Session unavailable');
    const back = `${window.location.origin}${returnPath.startsWith('/') ? returnPath : '/app/plan?from=portal'}`;
    const response = await fetch(
      `${getBaseUrl()}/api/subscription/create-portal-session?return_url=${encodeURIComponent(back)}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );

    if (!response.ok) await planFailure(response, 'Card & invoices couldn’t open. Nothing changed.');

    const data = await response.json();
    if (typeof data?.portal_url !== 'string' || !/^https:\/\//.test(data.portal_url)) {
      throw new Error('Card & invoices couldn’t open. Nothing changed.');
    }
    window.location.assign(data.portal_url);
  };

  const value: SubscriptionContextType = {
    subscription,
    allTiers,
    loading,
    error,
    isDev,
    usageSummary,
    usageSummaryLoading,
    usage,
    tierExtras,
    billingMode,
    refreshSubscription,
    refreshUsageSummary,
    applyUsage,
    applySubscription,
    createCheckoutSession,
    openCustomerPortal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};


export interface QuotaState {
  loading: boolean;
  /** null while loading or when the type is unknown to the server. */
  entry: UsageSummaryEntry | null;
  /** remaining !== 0 (unlimited counts as having quota). */
  hasQuota: boolean;
  /** True only when the server confirmed the quota is spent. */
  isSpent: boolean;
  refresh: () => Promise<void>;
}

/**
 * Quota-aware access for a counted usage type ("saved_creators", ...). The v5 chat bar and reports count are not
 * usage types: read them from `usage` (billing/useUsage). Hidden types (HIDDEN_USAGE_TYPES) always read as unknown.
 * Replaces tier-only gating on quota-bearing surfaces:
 * access means "has runs left", not "is on a paid tier". Display-only —
 * the server independently 403s when a run is actually out of quota.
 */
export const useQuota = (usageType: string): QuotaState => {
  const { usageSummary, usageSummaryLoading, refreshUsageSummary } = useSubscription();
  const entry = usageSummary?.[usageType] ?? null;
  return {
    loading: usageSummaryLoading,
    entry,
    hasQuota: entry ? entry.remaining !== 0 : !usageSummaryLoading ? true : false,
    isSpent: !!entry && entry.remaining === 0,
    refresh: refreshUsageSummary,
  };
};
